"use client";

import type { ReactNode } from "react";
import { usePathname } from "next/navigation";

import MobileBottomBarState from "./MobileBottomBarState";
import { isHindiMobileJourney, type MobileNavKey } from "./mobile-bottom-bar-path";
import { shouldShowMobileBottomBar } from "@/lib/global-overlays";

export { isMobileNavItemActive } from "./mobile-bottom-bar-path";

function NavItem({
  language,
  navKey,
  enHref,
  hiHref,
  enLabel,
  hiLabel,
  children,
}: {
  language: "en" | "hi";
  navKey: MobileNavKey;
  enHref: string;
  hiHref: string;
  enLabel: string;
  hiLabel: string;
  children: ReactNode;
}) {
  return (
    // The server render carries the page language's href and label. It used
    // to ship the English ones on Hindi pages and swap them after hydration,
    // so every Hindi page linked English URLs from this bar.
    <a
      href={language === "hi" ? hiHref : enHref}
      data-mobile-nav-key={navKey}
      data-en-href={enHref}
      data-hi-href={hiHref}
      data-en-label={enLabel}
      data-hi-label={hiLabel}
      data-journey-link={navKey === "journey" ? "" : undefined}
      className="flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 py-2.5 font-medium text-text-secondary transition-colors aria-[current=page]:font-semibold aria-[current=page]:text-intent-brand active:bg-surface-brand-subtle"
    >
      {children}
      <span data-mobile-nav-label className="text-xs">
        {language === "hi" ? hiLabel : enLabel}
      </span>
    </a>
  );
}

export default function MobileBottomBar() {
  const pathname = usePathname();

  // Report landings have one conversion decision and their own contextual
  // assistance. The global three-way nav otherwise covers the price/CTA on a
  // 390px viewport and introduces competing exits at the decision point.
  if (!shouldShowMobileBottomBar(pathname)) {
    return null;
  }
  const language = isHindiMobileJourney(pathname ?? "/") ? "hi" : "en";

  return (
    <nav
      id="mobile-bottom-navigation"
      lang={language}
      aria-label={language === "hi" ? "मुख्य मोबाइल नेविगेशन" : "Primary mobile navigation"}
      className="fixed bottom-0 left-0 right-0 z-50 flex border-t border-border-default bg-surface-raised md:hidden"
    >
      <MobileBottomBarState />
      <NavItem
        language={language}
        navKey="today"
        enHref="/panchang/"
        hiHref="/hi/panchang/"
        enLabel="Today"
        hiLabel="Aaj"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          aria-hidden
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18M8 14h3M13 14h3M8 18h3" strokeLinecap="round" />
        </svg>
      </NavItem>

      <span aria-hidden className="my-2 w-px self-stretch bg-border-default" />

      <NavItem
        language={language}
        navKey="ask"
        enHref="/ai-astrologer/"
        hiHref="/hi/muft-jyotish-chat/"
        enLabel="Ask Jyotishi"
        hiLabel="Jyotishi"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          aria-hidden
        >
          <path
            d="M21 12a8 8 0 01-8 8H7l-4 2 1.4-4.2A8.5 8.5 0 113 12"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M8 12h.01M12 12h.01M16 12h.01" strokeLinecap="round" />
        </svg>
      </NavItem>

      <span aria-hidden className="my-2 w-px self-stretch bg-border-default" />

      <NavItem
        language={language}
        navKey="journey"
        enHref="/account/"
        hiHref="/account/"
        enLabel="My Jyotishi"
        hiLabel="Mera Jyotishi"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          aria-hidden
        >
          <circle cx="12" cy="8" r="4" />
          <path d="M4 22a8 8 0 0116 0M18 4l1 1 2-2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </NavItem>
    </nav>
  );
}
